import { useEffect, useState } from "react";
import { Button, Card, Pill } from "./ui";
import { Ic } from "./icons";
import {
  FOLDER_COLORS,
  createFolder,
  updateFolder,
  type FolderColor,
  type FolderDoc,
} from "../lib/folders";

// Suggestions — emoji only, typed directly is still accepted.
const ICON_SUGGESTIONS = [
  "🏙️",
  "🏡",
  "🏢",
  "🏘️",
  "🌳",
  "🌊",
  "⛰️",
  "💼",
  "⭐",
  "🔥",
  "📍",
  "🗺️",
];

// Token → Tailwind swatch mapping. Kept here (not in folders.ts) because
// folders.ts should stay free of UI concerns.
export const FOLDER_SWATCH: Record<FolderColor, string> = {
  lime: "bg-lime-400",
  forest: "bg-[color:theme(colors.forest)]",
  brick: "bg-[color:theme(colors.brick)]",
  ink: "bg-ink-700",
  slate: "bg-slate-400",
  sky: "bg-sky-400",
};

export const FOLDER_TINT: Record<FolderColor, string> = {
  lime: "text-[color:theme(colors.lime.500)]",
  forest: "text-[color:theme(colors.forest)]",
  brick: "text-[color:theme(colors.brick)]",
  ink: "text-ink-700",
  slate: "text-slate-500",
  sky: "text-sky-500",
};

export const CreateFolderDialog = ({
  open,
  onClose,
  existingFolders,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  existingFolders: FolderDoc[];
  // If provided, the dialog edits this folder instead of creating a new one.
  editing?: FolderDoc | null;
}) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState<FolderColor>("lime");
  const [icon, setIcon] = useState<string>("");
  const [parentId, setParentId] = useState<string | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the dialog opens or switches between create/edit.
  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? "");
    setColor(editing?.color ?? "lime");
    setIcon(editing?.icon ?? "");
    setParentId(editing?.parentId ?? "");
    setError(null);
  }, [open, editing]);

  if (!open) return null;

  // Only root folders can be parents (2-level hierarchy enforced server-side too).
  // When editing a folder that already has children, forbid moving it under
  // another parent — the client-side rule in folders.ts would reject it.
  const selfHasChildren = editing
    ? existingFolders.some((f) => f.parentId === editing.id)
    : false;
  const availableParents = existingFolders.filter(
    (f) => !f.parentId && f.id !== editing?.id && !selfHasChildren,
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await updateFolder(editing.id, {
          name: name.trim(),
          color,
          icon: icon.trim() || null,
          parentId: parentId || null,
        });
      } else {
        await createFolder({
          name: name.trim(),
          color,
          icon: icon.trim() || null,
          parentId: parentId || null,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-[460px] p-7 shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <Pill tone="lime" icon={<Ic.Folder size={12} />}>
            {editing ? "Modifier le dossier" : "Nouveau dossier"}
          </Pill>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500"
          >
            <Ic.Cross size={14} />
          </button>
        </div>
        <h2 className="mt-3 text-[20px] font-semibold tracking-tight">
          {editing ? "Ajuster l'apparence" : "Organisez vos fiches"}
        </h2>
        <p className="mt-1 text-[13px] text-ink-600">
          Une fiche peut appartenir à plusieurs dossiers — utile pour croiser
          ville, type de bien ou statut commercial.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <label className="block">
            <span className="text-[11.5px] text-ink-500">Nom du dossier</span>
            <input
              type="text"
              autoFocus
              required
              maxLength={40}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Paris, Maisons, Clients VIP…"
              className="mt-1 w-full bg-paper hairline rounded-xl2 h-11 px-3 text-[13.5px] outline-none focus:ring-2 focus:ring-lime-300"
            />
          </label>

          <div>
            <span className="text-[11.5px] text-ink-500">Couleur</span>
            <div className="mt-2 flex items-center gap-2">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full ${FOLDER_SWATCH[c]} transition ${
                    color === c
                      ? "ring-2 ring-offset-2 ring-ink-900"
                      : "hover:ring-2 hover:ring-offset-2 hover:ring-ink-300"
                  }`}
                  aria-label={c}
                />
              ))}
            </div>
          </div>

          <div>
            <span className="text-[11.5px] text-ink-500">Icône (emoji — optionnel)</span>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <input
                type="text"
                maxLength={4}
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="—"
                className="w-12 h-10 text-center bg-paper hairline rounded-lg text-[16px] outline-none focus:ring-2 focus:ring-lime-300"
              />
              {ICON_SUGGESTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setIcon(emoji)}
                  className={`w-9 h-9 rounded-lg text-[16px] transition ${
                    icon === emoji
                      ? "bg-lime-200"
                      : "bg-paper hairline hover:bg-ink-50"
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {availableParents.length > 0 && (
            <label className="block">
              <span className="text-[11.5px] text-ink-500">
                Dossier parent (optionnel)
              </span>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="mt-1 w-full bg-paper hairline rounded-xl2 h-11 px-3 text-[13.5px] outline-none focus:ring-2 focus:ring-lime-300"
              >
                <option value="">— Aucun (dossier racine) —</option>
                {availableParents.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.icon ? `${f.icon} ` : ""}
                    {f.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] text-ink-500">
                Hiérarchie limitée à 2 niveaux.
              </span>
            </label>
          )}

          {error && (
            <div className="text-[12px] text-rose-700 bg-rose-50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={busy}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="dark"
              size="sm"
              disabled={busy || !name.trim()}
              iconRight={<Ic.Arrow size={14} />}
            >
              {busy ? "…" : editing ? "Enregistrer" : "Créer le dossier"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
